import * as React from 'react';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { antGrey, releaseStepDetailsContainerModuleStyle, runwayColors } from '../styles/globalStyles';
import { ReleaseStepComponentProps } from '../../typescriptHelpers';
import { CustomMessageConfigKey, TokenizedTextModalType } from '../../types';
import { CheckOutlined, CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectCustomMessagesConfig, selectIsReleaseReleased, selectRelease } from '../../selectors/selectors';
import { tryUpdateRelease, UpdateCustomizedReleaseSummaryMessageRequest } from '../../actions/release';
import { ReleaseUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { TokenizedTextEditModal } from '../TokenizedTextEditModal';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface ReleaseSummaryMessageComponentProps extends ReleaseStepComponentProps {
    version: string;
    onShowEditReleaseSummaryMessage?: () => void;
}

export interface ReleaseSummaryMessageComponentBaseProps extends ReleaseSummaryMessageComponentProps {
    showEditModal: boolean;
    setShowEditModal: (show: boolean) => void;
    backgroundColor?: string;
}

export const ReleaseSummaryMessageComponentBase: React.FC<ReleaseSummaryMessageComponentBaseProps> = (props) => {
    const updateMessageIsLoading = useIsLoading(ReleaseUpdatable.CustomizeReleaseSummaryMessage, props.releaseId);
    const updateMessageError = useError(ReleaseUpdatable.CustomizeReleaseSummaryMessage, props.releaseId);
    const updateMessageSuccess = useUpdated(ReleaseUpdatable.CustomizeReleaseSummaryMessage, props.releaseId);

    const [copySuccess, setCopySuccess] = React.useState(false);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setCopySuccess(false);
        }, 2000);

        return () => clearInterval(interval);
    }, [copySuccess]);

    const copyToClipboard = async (key) => {
        try {
            await navigator.clipboard.writeText(key);
            setCopySuccess(true);
        } catch {
            setCopySuccess(false);
        }
    };

    const savedMessage = useSelector((state: RootState) => {
        const release = selectRelease(state, props.releaseId);
        if (!release.releaseSummaryMessage) {
            return 'Release summary not available.';
        } else {
            return release.releaseSummaryMessage.message;
        }
    });
    const [message, setMessage] = React.useState(savedMessage);

    const isReleased = useSelector((state: RootState) => selectIsReleaseReleased(state, props.releaseId));

    React.useEffect(() => {
        if (updateMessageError) {
            globalMessage.error(updateMessageError);
        }
    }, [updateMessageError]);

    React.useEffect(() => {
        if (updateMessageSuccess) {
            globalMessage.success(`Release summary message has been updated on the release.`);
            props.setShowEditModal(false);
        }
    }, [updateMessageSuccess]);

    React.useEffect(() => {
        if (savedMessage) {
            setMessage(savedMessage);
        }
    }, [savedMessage]);

    const dispatch = useDispatch();

    const updateReleaseSummaryMessage = () => {
        const request: UpdateCustomizedReleaseSummaryMessageRequest = {
            appId: props.appId,
            orgId: props.orgId,
            releaseId: props.releaseId,
            message: message,
        };
        dispatch(tryUpdateRelease(ReleaseUpdatable.CustomizeReleaseSummaryMessage, request, props.releaseId));
    };

    const customMessagesConfig = useSelector((state: RootState) => selectCustomMessagesConfig(state, props.appId));

    const templateData = customMessagesConfig.customMessageTemplatesData[CustomMessageConfigKey.ReleaseSummaryMessage];

    const availableTokens = templateData?.availableTokens ?? [];

    const titleText = `Edit release summary for ${props.version}`;

    return (
        <>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Space direction="vertical" size={20} style={{ width: '100%', minWidth: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 20 }}>
                        <div
                            style={{
                                ...releaseStepDetailsContainerModuleStyle,
                                backgroundColor: props.backgroundColor ?? antGrey.grey2,
                                whiteSpace: 'pre-wrap',
                                display: 'flex',
                                flexDirection: 'row',
                                flexGrow: 1,
                                gap: 20,
                                width: '100%',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    gap: 20,
                                    width: '100%',
                                }}
                            >
                                {message}
                            </div>

                            <div style={{ marginLeft: '20', marginTop: '20' }}>
                                <Tooltip overlay="Copied!" open={copySuccess}>
                                    <Button
                                        onClick={() => copyToClipboard(message)}
                                        icon={
                                            copySuccess ?
                                                <CheckOutlined style={{ color: runwayColors.statusGreen }} />
                                            :   <CopyOutlined style={{ color: runwayColors.darkTextGrey }} />
                                        }
                                        type="link"
                                    ></Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </Space>
            </div>
            {!isReleased && (
                <div>
                    <InfoCircleOutlined style={{ color: antGrey.grey7, padding: 8 }} />
                    <Text type="secondary">The final token values will be shown once the release is complete.</Text>
                </div>
            )}
            <TokenizedTextEditModal
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                open={props.showEditModal}
                onCancel={() => props.setShowEditModal(false)}
                applyChangesIsLoading={updateMessageIsLoading}
                onSubmit={updateReleaseSummaryMessage}
                availableTokens={availableTokens}
                maxLength={templateData?.maxCharCount ?? 1000}
                titleText={titleText}
                text={message}
                setText={setMessage}
                modalType={TokenizedTextModalType.ReleaseSummaryMessage}
            />
        </>
    );
};
